/**
 * useCurrentUser Hook
 * Firebase Auth 유저 + Firestore 프로필 + 역할 + 권한을 통합 관리
 */

import { useState, useEffect } from 'react';
import { User as FirebaseUser, onAuthStateChanged } from 'firebase/auth';
import {
  doc,
  onSnapshot,
  collection,
  query,
  where,
  getDocs,
  Timestamp,
  Firestore,
} from 'firebase/firestore';
import { auth, db } from '../../firebase/config';
import {
  UserProfile,
  UserWithRoles,
  Role,
  RoleAssignment,
  Permission,
  UseCurrentUserResult,
  AssignmentTargetType,
} from '../../types/auth';

/**
 * 현재 로그인한 사용자의 프로필, 역할, 권한을 실시간으로 구독
 *
 * @returns {UseCurrentUserResult} user, isLoading, error
 */
export function useCurrentUser(): UseCurrentUserResult {
  const [user, setUser] = useState<UserWithRoles | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Firebase가 초기화되지 않은 경우 데모 사용자 반환
    if (!auth || !db) {
      const demoUser: UserWithRoles = {
        profile: {
          id: 'demo-user',
          email: 'demo@ngle.co.kr',
          displayName: '데모 사용자',
          status: 'active',
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
          createdBy: 'system',
          updatedBy: 'system',
        },
        roles: ['super_admin'],
        permissions: ['*'],
      };
      setUser(demoUser);
      setIsLoading(false);
      return () => {}; // 빈 cleanup 함수
    }

    // Firebase가 초기화되었으므로 db는 null이 아님
    const dbInstance = db;
    let unsubscribeProfile: (() => void) | null = null;

    // Firebase Auth 상태 변화 구독
    const unsubscribeAuth = onAuthStateChanged(
      auth,
      async (firebaseUser: FirebaseUser | null) => {
        try {
          // 로그아웃 상태
          if (!firebaseUser) {
            setUser(null);
            setIsLoading(false);
            if (unsubscribeProfile) {
              unsubscribeProfile();
              unsubscribeProfile = null;
            }
            return;
          }

          const uid = firebaseUser.uid;

          // Firestore users 문서 실시간 구독
          const userDocRef = doc(dbInstance, 'users', uid);

          unsubscribeProfile = onSnapshot(
            userDocRef,
            async (userSnapshot) => {
              try {
                if (!userSnapshot.exists()) {
                  setUser(null);
                  setIsLoading(false);
                  return;
                }

                // UserProfile 데이터
                const profile = {
                  id: userSnapshot.id,
                  ...userSnapshot.data(),
                } as UserProfile;

                // 역할 할당 조회 (auth_roleAssignments)
                const roleAssignments = await fetchUserRoleAssignments(dbInstance, uid, profile);

                // 역할 ID 목록
                const roleIds = roleAssignments.map((assignment) => assignment.roleId);

                // 역할 문서들 조회 (auth_roles)
                const roles = await fetchRoles(dbInstance, roleIds);

                // 모든 역할의 권한을 합침
                const permissions = mergePermissions(roles);

                // 최종 UserWithRoles 객체
                const userWithRoles: UserWithRoles = {
                  profile,
                  roles: roleIds,
                  permissions,
                };

                setUser(userWithRoles);
                setError(null);
                setIsLoading(false);
              } catch (err) {
                console.error('역할/권한 조회 오류:', err);
                setError(err as Error);
                setIsLoading(false);
              }
            },
            (err) => {
              console.error('Firestore users 구독 오류:', err);
              setError(err as Error);
              setIsLoading(false);
            }
          );
        } catch (err) {
          console.error('Auth 상태 변화 처리 오류:', err);
          setError(err as Error);
          setIsLoading(false);
        }
      },
      (err) => {
        console.error('Auth 구독 오류:', err);
        setError(err as Error);
        setIsLoading(false);
      }
    );

    // Cleanup
    return () => {
      unsubscribeAuth();
      if (unsubscribeProfile) {
        unsubscribeProfile();
      }
    };
  }, []);

  return { user, isLoading, error };
}

/**
 * 사용자의 역할 할당 조회
 * (개인 할당 + 직급 할당 + 팀 할당 + 조직 할당)
 *
 * V144: users.roleIds fallback 완전 제거
 * - 역할 판정의 유일한 진실 소스는 auth_roleAssignments
 * - users.roleIds는 더 이상 권한/역할 판정에 사용하지 않음
 */
async function fetchUserRoleAssignments(
  db: Firestore,
  userId: string,
  profile: UserProfile
): Promise<RoleAssignment[]> {
  const assignments: RoleAssignment[] = [];
  const addedRoleIds = new Set<string>(); // 중복 방지

  try {
    // 1. 개인 할당 (targetType: 'user')
    const userAssignmentsQuery = query(
      collection(db, 'auth_roleAssignments'),
      where('targetType', '==', AssignmentTargetType.USER),
      where('targetId', '==', userId),
      where('status', '==', 'active')
    );
    const userAssignmentsSnapshot = await getDocs(userAssignmentsQuery);
    userAssignmentsSnapshot.docs.forEach((doc) => {
      const data = doc.data();
      if (!addedRoleIds.has(data.roleId)) {
        addedRoleIds.add(data.roleId);
        assignments.push({ id: doc.id, ...data } as RoleAssignment);
      }
    });

    // 2. 직급 할당 (targetType: 'position')
    if (profile.positionId) {
      const positionAssignmentsQuery = query(
        collection(db, 'auth_roleAssignments'),
        where('targetType', '==', AssignmentTargetType.POSITION),
        where('targetId', '==', profile.positionId),
        where('status', '==', 'active')
      );
      const positionAssignmentsSnapshot = await getDocs(positionAssignmentsQuery);
      positionAssignmentsSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        if (!addedRoleIds.has(data.roleId)) {
          addedRoleIds.add(data.roleId);
          assignments.push({ id: doc.id, ...data } as RoleAssignment);
        }
      });
    }

    // 3. 팀 할당 (targetType: 'team')
    if (profile.teamId) {
      const teamAssignmentsQuery = query(
        collection(db, 'auth_roleAssignments'),
        where('targetType', '==', AssignmentTargetType.TEAM),
        where('targetId', '==', profile.teamId),
        where('status', '==', 'active')
      );
      const teamAssignmentsSnapshot = await getDocs(teamAssignmentsQuery);
      teamAssignmentsSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        if (!addedRoleIds.has(data.roleId)) {
          addedRoleIds.add(data.roleId);
          assignments.push({ id: doc.id, ...data } as RoleAssignment);
        }
      });
    }

    // 4. 조직 할당 (targetType: 'orgUnit')
    if (profile.orgUnitId) {
      const orgAssignmentsQuery = query(
        collection(db, 'auth_roleAssignments'),
        where('targetType', '==', AssignmentTargetType.ORG_UNIT),
        where('targetId', '==', profile.orgUnitId),
        where('status', '==', 'active')
      );
      const orgAssignmentsSnapshot = await getDocs(orgAssignmentsQuery);
      orgAssignmentsSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        if (!addedRoleIds.has(data.roleId)) {
          addedRoleIds.add(data.roleId);
          assignments.push({ id: doc.id, ...data } as RoleAssignment);
        }
      });
    }
  } catch (err) {
    // V144: auth_roleAssignments 조회 실패 시 빈 배열 반환 (users.roleIds fallback 제거됨)
    console.error('[V144] auth_roleAssignments 조회 실패:', err);
  }

  return assignments;
}

/**
 * 역할 ID 목록으로 역할 문서들 조회
 */
async function fetchRoles(db: Firestore, roleIds: string[]): Promise<Role[]> {
  if (roleIds.length === 0) {
    return [];
  }

  const roles: Role[] = [];

  // Firestore 'in' 쿼리는 최대 10개까지만 지원
  // 10개씩 나눠서 조회
  const chunks = chunkArray(roleIds, 10);

  for (const chunk of chunks) {
    const rolesQuery = query(
      collection(db, 'auth_roles'),
      where('__name__', 'in', chunk),
      where('status', '==', 'active')
    );
    const rolesSnapshot = await getDocs(rolesQuery);
    rolesSnapshot.docs.forEach((doc) => {
      roles.push({ id: doc.id, ...doc.data() } as Role);
    });
  }

  return roles;
}

/**
 * 역할들의 권한을 합침 (중복 제거)
 */
function mergePermissions(roles: Role[]): Permission[] {
  const permissionSet = new Set<Permission>();

  roles.forEach((role) => {
    role.permissions.forEach((permission) => {
      permissionSet.add(permission);
    });
  });

  return Array.from(permissionSet);
}

/**
 * 배열을 지정된 크기로 분할
 */
function chunkArray<T>(array: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
}
